import { OnInit, Component, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { PharmacyService } from '../../../services/pharmacyservice';
import { ToastrService } from 'ngx-toastr';
import { PharmacyCRUDModel } from '../../DTO/pharmacy-crud';
import { formatDate } from "@angular/common";
import {MatExpansionModule} from '@angular/material/expansion';
import { VendorDropdownData, Vendor } from '../../DTO/vendor';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ConnectionDetails } from '../../DTO/pharmacy';
import { VendorService } from 'src/app/services/vendorservice';
import { ConnectionDetailsComponent } from '../../connection/connection-details/connection-details.component';

@Component({
  selector: 'app-pharmacy-add',
  templateUrl: './pharmacy-add.component.html',
  styleUrls: ['./pharmacy-add.component.css']
})
export class PharmacyAddComponent implements OnInit {
  isLoading: boolean = false;
  form: FormGroup;
  PharmacyId: FormControl;
  PharmacyName: FormControl;
  EffectiveDate: FormControl;
  TermDate: FormControl;
  IsEnabled: FormControl;
  PetEnabled: FormControl;
  BillingGroup: FormControl;
  BillingRegion: FormControl;
  PID: FormControl;
  Vendor: FormControl;
  pharmacyModel: PharmacyCRUDModel = new PharmacyCRUDModel();
  vendorList:any;
  useVendorLevelConnectivity = false;
  connectionDetails: ConnectionDetails;
  useOkta:boolean=false;
  userName: string='';
  minEffectiveDate:Date=new Date();
  minTermDate:Date=new Date();
  panelOpenState = false;
  connectionMessage:string='';
  missingParameter = false;
  isConnectionSuccessful = false;
  UseConnectionDetails: FormControl;
  connectionDetailsAttributeModel: any = {};
  VendorIsConnectivitySet = false;
  @ViewChild(ConnectionDetailsComponent) connectionDetailsComponent: ConnectionDetailsComponent;
  cdr: ChangeDetectorRef;

  constructor(
    private pharmacyService: PharmacyService,
    private vendorService: VendorService,
    private toastr: ToastrService,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.cdr = changeDetectorRef;
  }

  ngOnInit() {
    this.pharmacyModel.AuthType = "BASIC";
    this.pharmacyModel.PharmacyEndpointURL = this.pharmacyModel.PharmacyEndpointURL || "";
    this.minTermDate.setDate(new Date().getDate()+1);
    this.createFormControls();
    this.createForm();
    this.userName = localStorage.getItem("loggedName");
    this.LoadVendors();
    this.evaluateConnectivityConfiguration();
  }

  updateVendorConnectivityFlag(): void {
    const selectedVendorId = this.form.get('Vendor')?.value;
    const selectedVendor = this.vendorList?.find((v: Vendor) => v.VendorId === selectedVendorId);
    this.VendorIsConnectivitySet = selectedVendor?.IsConnectivitySet?.toString().toLowerCase() === 'true';
  }
  
  onVendorChange(vendorId: any): void {
    const selectedVendorIdStr = String(vendorId).trim();
    const selectedVendor = this.vendorList?.find((v: Vendor) => String(v.VendorId) === selectedVendorIdStr);

    // Update connectivity flag
    this.VendorIsConnectivitySet = selectedVendor?.IsConnectivitySet?.toString().toLowerCase() === 'true';

    // Reset the toggle
    this.useVendorLevelConnectivity = false;
    this.form.get('UseVendorConnectionDetails')?.setValue(false, { emitEvent: false });

    // Clear any previous warning messages
    this.connectionMessage = '';

    // Update form validation
    this.Vendor.updateValueAndValidity();

    // UI to re-evaluate bindings
    this.cdr.detectChanges();
  }

evaluateConnectivityConfiguration(): void {
  if (this.pharmacyModel.IsConnectivitySet === 'false' && this.useVendorLevelConnectivity) {
    this.connectionMessage = "Vendor connectivity settings are missing. Please complete the configuration on the 'Vendor' page before proceeding.";
    this.toastr.warning(this.connectionMessage, '', { timeOut: 10000 });
  } else if (this.pharmacyModel.IsConnectivitySet === 'true') {
    this.connectionMessage = '';
  }
}

  isUpdateButtonDisabled(): boolean {
    if (this.useVendorLevelConnectivity) {
      return !this.form.valid ||
            !this.VendorIsConnectivitySet ||
            !this.form.get('Vendor')?.value;
    } else {
      const panelOpen = this.connectionDetailsComponent?.panelOpenState;
      const connectionFormValid = this.connectionDetailsComponent?.form?.valid;

      // ✅ Only validate connection form if panel is open
      const connectionFormInvalid = panelOpen && !connectionFormValid;
      return !this.form.valid || connectionFormInvalid;
    }
  }
  
  createFormControls() {
    this.PharmacyId = new FormControl();
    this.PharmacyName = new FormControl('', [Validators.required, Validators.maxLength(50)]);
    this.EffectiveDate = new FormControl(new Date(new Date().toDateString()), Validators.required);
    this.TermDate = new FormControl();
    this.IsEnabled = new FormControl();
    this.PetEnabled = new FormControl(false);
    this.BillingGroup = new FormControl('', [Validators.maxLength(4),Validators.pattern("[0-9]+$")]);
    this.BillingRegion = new FormControl('', [Validators.maxLength(2),Validators.pattern("[0-9]+$")]);
    this.PID = new FormControl('', [Validators.required,Validators.maxLength(20),Validators.pattern("^[a-zA-Z0-9]*$")]);
    this.Vendor = new FormControl('', [Validators.required]);
    this.UseConnectionDetails = new FormControl(false);
  }

  createForm() {
    this.form = new FormGroup({
      PharmacyId: this.PharmacyId,
      PharmacyName: this.PharmacyName,
      EffectiveDate: this.EffectiveDate,
      TermDate: this.TermDate,
      IsEnabled: this.IsEnabled,
      PetEnabled: this.PetEnabled,
      BillingGroup: this.BillingGroup,
      BillingRegion: this.BillingRegion,
      PID: this.PID,
      Vendor: this.Vendor,
      UseConnectionDetails: this.UseConnectionDetails
    });
  }

  get errorMessageForPharmacyName(): string {
    const pharmacyform: FormControl = (this.form.get('PharmacyName') as FormControl);
    return pharmacyform.hasError('required') ?
      'Pharmacy chain name is required' :
      pharmacyform.hasError('maxlength') ?
      'Max length is 50 characters' :
      pharmacyform.hasError('duplicate') ?
      'Pharmacy chain name already exist' :"" 
  }

  get errorMessageForEffectiveDate():string{
    const pharmacyform: FormControl = (this.form.get('EffectiveDate') as FormControl);
    return pharmacyform.hasError('matDatepickerParse') ?
    'Invalid date' :
    pharmacyform.hasError('required') ?
    'Activation date is required' :
    pharmacyform.hasError('date error') ?
    'Activation date should be less than deactivation date': 
    pharmacyform.hasError('min date') ?
    'Activation date should be greater than equal to today date':""
  }

  get errorMessageForTermDate():string{
    const pharmacyform: FormControl = (this.form.get('TermDate') as FormControl);
    return pharmacyform.hasError('matDatepickerParse') ?
    'Invalid date' :
    pharmacyform.hasError('date error') ?
    'Deactivation date should be greater than activation date':""
  }

  get errorMessageForEndpointURL():string{
    const pharmacyform: FormControl = (this.form.get('PharmacyEndpointURL') as FormControl);
    return pharmacyform.hasError('required') ?
    'Endpoint url is required' :""
  }

  get errorMessageForBillingGroup(): string {
    const pharmacyform: FormControl = (this.form.get('BillingGroup') as FormControl);
    return pharmacyform.hasError('maxlength') ?
      'Max length is 4 characters' :
      pharmacyform.hasError('pattern') ?
      'Please enter only numeric values' :"" 
  }

  get errorMessageForBillingRegion(): string {
    const pharmacyform: FormControl = (this.form.get('BillingRegion') as FormControl);
    return pharmacyform.hasError('maxlength') ?
      'Max length is 2 characters' :
      pharmacyform.hasError('pattern') ?
      'Please enter only numeric values' :"" 
  }

  get errorMessageForPID(): string {
    const pharmacyform: FormControl = (this.form.get('PID') as FormControl);
    return pharmacyform.hasError('required') ?
      'PID is required' :
      pharmacyform.hasError('maxlength') ?
      'Max length is 20 characters' :
      pharmacyform.hasError('pattern') ?
      'Please enter only alpha numeric values' :
      pharmacyform.hasError('duplicate') ?
      'PID already exist' :"" 
  }

  get errorMessageForVendor():string{
    const vendorform: FormControl = (this.form.get('Vendor') as FormControl);
    return vendorform.hasError('required') ?
      'Vendor is required' :""
  }

  clearPharmacyNameDuplicateValidation(){
    this.PharmacyName.setErrors({'duplicate': null});
    this.PharmacyName.updateValueAndValidity();
  }

  clearPIDDuplicateValidation(){
    this.PID.setErrors({'duplicate': null});
    this.PID.updateValueAndValidity();
  }

  toggleVendorLevelConnectivity(isVendorLevel: boolean): void {
    setTimeout(() => {
      this.useVendorLevelConnectivity = isVendorLevel;
      this.updateVendorConnectivityFlag();

      const selectedVendorId = this.form.get('Vendor')?.value;
      if (this.useVendorLevelConnectivity) {
        if (!selectedVendorId) {
          this.connectionMessage = "Please select a Vendor before enabling vendor-level connectivity.";
          this.toastr.warning(this.connectionMessage);
        } else if (!this.VendorIsConnectivitySet) {
          this.connectionMessage = "Vendor connectivity settings are missing. Please complete the configuration on the 'Vendor' page before proceeding.";
          this.toastr.warning(this.connectionMessage, '', { timeOut: 10000 });
        } else {
          this.connectionMessage = '';
        }
      } else {
        this.connectionMessage = '';
      }
    }, 0);
  }

  LoadVendors() {
    this.vendorService.getVendors().subscribe((vendors) => {
    this.vendorList = vendors;
    });
  }

  dateValidation()
  {
     let effectiveDate=new Date(Date.parse(this.form.get('EffectiveDate')?.value))
     let termDate=new Date(Date.parse(this.form.get('TermDate')?.value))
     this.clearValidation();
     if(effectiveDate<new Date(new Date().setHours(0,0,0,0))) {
        this.EffectiveDate.setErrors({'min date': true});
        this.form.get('EffectiveDate')?.markAsTouched();
        return;
     }
    
      if(this.form.get('TermDate')?.value!=null &&
      this.form.get('EffectiveDate')?.value!=null && 
      effectiveDate>=termDate)
      {
        this.EffectiveDate.setErrors({'date error': true});
        this.TermDate.setErrors({'date error': true});
        this.form.get('EffectiveDate')?.markAsTouched();
        this.form.get('TermDate')?.markAsTouched();
      }
  }

  clearValidation(){
    this.EffectiveDate.setErrors({'date error': null});
     this.EffectiveDate.setErrors({'min date': null});
     this.TermDate.setErrors({'date error': null});
     this.EffectiveDate.updateValueAndValidity();
     this.TermDate.updateValueAndValidity();
  }

  onDateInputkeyup(event:any)
  {
    this.form.get('EffectiveDate')?.markAsTouched();
    this.form.get('TermDate')?.markAsTouched();
    this.dateValidation();
  }

  onDateSelect(event:any){
    if(event.targetElement.name=="EffectiveDate"){
      this.minTermDate.setDate(new Date(Date.parse(this.form.get('EffectiveDate')?.value)).getDate()+1);
    }
    this.dateValidation();
  }

  submitForm() {
    if (this.form.valid) {
      this.isLoading = true;
      this.getPharmacyModel();

      if (this.useVendorLevelConnectivity) {
        this.pharmacyModel.IsConnectivitySet = "false";
        this.pharmacyModel.IsVendorConnectionEnabled = "true";
      } else {
        this.pharmacyModel.IsVendorConnectionEnabled = "false";
        // If vendor level toggle is OFF, check connection details panel state
        if (this.connectionDetailsComponent?.panelOpenState) {
          this.pharmacyModel.IsConnectivitySet = "true";
          this.connectionDetailsComponent.setModelAttributes();

          this.pharmacyModel = {
            ...this.pharmacyModel,
            PharmacyEndpointURL: this.connectionDetailsComponent.form.value.AuthType === "BASIC" 
              ? this.connectionDetailsComponent.form.value.BasicEndpointURL 
              : this.connectionDetailsComponent.form.value.OktaEndpointURL,
            AuthType: this.connectionDetailsComponent.form.value.AuthType,
            OktaMckClientId: this.connectionDetailsComponent.form.value.OktaMckClientId,
          };

          // Conditionally add fields based on AuthType
          if (this.connectionDetailsComponent.form.value.AuthType === "BASIC") {
            Object.assign(this.pharmacyModel, {
              BasicUserName: this.connectionDetailsComponent.form.value.BasicUserName,
              BasicPassword: this.connectionDetailsComponent.form.value.BasicPassword,
              HiddenPassword: this.connectionDetailsComponent.form.value.HiddenPassword,
            });
          } else if (this.connectionDetailsComponent.form.value.AuthType === "OKTA") {
            Object.assign(this.pharmacyModel, {
              OktaAuthURL: this.connectionDetailsComponent.form.value.OktaAuthURL,
              OktaClientId: this.connectionDetailsComponent.form.value.OktaClientId,
              OktaClientSecret: this.connectionDetailsComponent.form.value.OktaClientSecret,
              HiddenClientSecret: this.connectionDetailsComponent.form.value.HiddenClientSecret,
            });
          }
        } else {
          this.pharmacyModel.IsConnectivitySet = "false";
        }
      }
      this.pharmacyService.savePharmacy(this.pharmacyModel).subscribe(res => {
          this.isLoading = false;
        if(res.ResponseStatus=='1'){
            this.toastr.success('Pharmacy chain saved successfully');
            this.router.navigate(['searchpharmacy']);
        }
        else if(res.ResponseStatus=='0' && res.ResponseMessage!=''){
            this.toastr.error(res.ResponseMessage);
          }
      },(error: any)=>{
        this.isLoading=false;
        if(Array.isArray(error)){
          for (var err of error){
            if(err.ErrorMessage=='Pharmacy Duplicate'){
              this.PharmacyName.setErrors({'duplicate': true});
              this.form.get('PharmacyName')?.markAsTouched();
            }
            else if(err.ErrorMessage=='PID Duplicate'){
              this.PID.setErrors({'duplicate': true});
              this.form.get('PID')?.markAsTouched();
            }
          }
        }
        else if(error=='Pharmacy Duplicate'){
          this.PharmacyName.setErrors({'duplicate': true});
          this.form.get('PharmacyName')?.markAsTouched();
        }
        else if(error=='PID Duplicate'){
          this.PID.setErrors({'duplicate': true});
          this.form.get('PID')?.markAsTouched();
        }
        else{
          this.toastr.error(error);
        }
      });
    }
  }

  getPharmacyModel(){
    this.pharmacyModel=new PharmacyCRUDModel();
    this.pharmacyModel.VendorId = this.form.get('Vendor')?.value;
    this.pharmacyModel.PharmacyName = this.form.get('PharmacyName')?.value;
    this.pharmacyModel.EffectiveDate =formatDate(this.form.get('EffectiveDate')?.value, 'yyyy-MM-dd', 'en-US');
    if(this.form.get('TermDate')?.value!=null){
      this.pharmacyModel.TermDate =formatDate(this.form.get('TermDate')?.value, 'yyyy-MM-dd', 'en-US');
    }
    this.pharmacyModel.IsEnabled = '1';
    this.pharmacyModel.PetEnabled = this.form.get('PetEnabled')?.value==true?"1":"0";
    this.pharmacyModel.BillingGroup = this.form.get('BillingGroup')?.value;
    this.pharmacyModel.BillingRegion = this.form.get('BillingRegion')?.value;
    this.pharmacyModel.PharmacyEndpointURL = this.form.get('PharmacyEndpointURL')?.value;
    // this.pharmacyModel.BasicPassword = this.form.get('BasicPassword')?.value;
    this.pharmacyModel.PID = this.form.get('PID')?.value;
    this.pharmacyModel.UpdatedBy=this.userName;
  }

  onClickCancel(){
    this.router.navigate(['searchpharmacy']);
  }

  onBillingGroupChange(event:any){
    let leadingZeros = "";
    if (event.target.value.length==1){
      leadingZeros = "000";
    }
    else if (event.target.value.length==2){
      leadingZeros = "00";
    }
    else if (event.target.value.length==3){
      leadingZeros = "0";
    }
    this.form.get('BillingGroup')?.setValue(leadingZeros+this.form.get('BillingGroup')?.value);
  }

  onBillingRegionChange(event:any){
    if (event.target.value.length==1){
      this.form.get('BillingRegion')?.setValue("0"+this.form.get('BillingRegion')?.value);
    }
  }
}

